import '@geoapify/geocoder-autocomplete/styles/round-borders.css';
import { GeoapifyGeocoderAutocomplete } from '@geoapify/react-geocoder-autocomplete';
import { memo, useEffect, useMemo, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import timezones from 'timezones-list';
import { Info } from '../../../assets/icons';
import { IconSize } from '../../../constant/IconSize.constant';
import { Country, State } from '../../../lib/country-state';
import { Dropdown } from '../Dropdown/Dropdown.component';
import { Icon } from '../Icon.component';
import { FormInput, InputType, ValidationType } from '../Input.component';
import { Label, LabelType } from '../Label.component';
import { ColorType } from '../shared/Colors.g';
import { Tooltip, TooltipContent, TooltipTrigger } from '../Tooltip.component';

export interface AddressValue {
  street?: string;
  streetNumber?: string;
  unit?: string;
  city?: string;
  zipOrPostalCode?: string;
  stateOrRegion?: string;
  timeZone?: string;
  latitude?: string | number;
  longitude?: string | number;
  isoCountryCode?: string;
}

const mapGeoapifyToAddress = (place: any, isoCountryCode?: string): Partial<AddressValue> => {
  const props = place?.properties ?? {};
  const housenumber = props.housenumber !== null ? String(props.housenumber) : '';
  const split = housenumber.includes('-') ? housenumber.split('-', 2) : null;
  const unit = split && split.length > 1 ? split[0] : '';
  const streetNumber = split && split.length > 1 ? split[1] : housenumber;

  return {
    stateOrRegion: props.state_code,
    timeZone: props?.timezone?.name,
    city: props.city,
    zipOrPostalCode: props.postcode,
    street: props.street,
    streetNumber,
    unit,
    latitude: props.lat,
    longitude: props.lon,
    isoCountryCode,
  };
};

export type AddressAutocompleteProps = {
  value: AddressValue;
  onChange: (next: Partial<AddressValue>) => void;
  countryIsoCode: string;
  countryName?: string;
  include?: {
    unit?: boolean;
    state?: boolean;
    country?: boolean;
    timezone?: boolean;
    geo?: boolean;
  };
  errors?: {
    zip?: boolean;
    state?: boolean;
    city?: boolean;
    street?: boolean;
    streetNumber?: boolean;
  };
  errorLabels?: {
    zip?: string;
    state?: string;
    city?: string;
    street?: string;
    streetNumber?: string;
  };
  labels?: {
    addressLabel?: string;
    manualToggleLabel?: string;
    street?: string; // fallback for streetName
    streetName?: string;
    streetNumber?: string;
    city?: string;
    state?: string;
    zip?: string;
    country?: string;
    timezone?: string;
    lat?: string;
    lon?: string;
    searchPlaceholder?: string;
    backToSearchLabel?: string;
  };
  stateRequired?: boolean;
  showStateOptionalHint?: boolean;
  defaultMode?: 'search' | 'manual';
  onModeChange?: (mode: 'search' | 'manual') => void;
  debounceMs?: number;
  countryDropdownItems?: any[];
  onCountryChange?: (country: any) => void;
};

export const AddressAutocomplete = memo(
  ({
    value,
    onChange,
    countryIsoCode,
    countryName,
    include = {
      unit: true,
      state: true,
      country: true,
      timezone: true,
      geo: true,
    },
    errors,
    errorLabels,
    labels,
    stateRequired,
    showStateOptionalHint = true,
    defaultMode,
    onModeChange,
    debounceMs = 300,
    countryDropdownItems,
    onCountryChange,
  }: AddressAutocompleteProps) => {
    const { t } = useTranslation();
    const resolvedCountryName = useMemo(() => {
      if (countryName) return countryName;
      const found = Country.getAllCountries().find((c: any) => c.isoCode === countryIsoCode);
      return found?.name || countryIsoCode;
    }, [countryIsoCode, countryName]);

    const [mode, setMode] = useState<'search' | 'manual'>(() => {
      if (defaultMode) return defaultMode;
      const hasAddress = !!(value?.street || value?.city || value?.zipOrPostalCode);
      return hasAddress ? 'manual' : 'search';
    });

    useEffect(() => {
      onModeChange && onModeChange(mode);
    }, [mode, onModeChange]);

    const provinceList = useMemo(() => {
      return State.getStatesOfCountry(countryIsoCode || 'CA').map((obj: any) => ({
        ...obj,
        id: obj.isoCode,
        selected: obj.isoCode === value?.stateOrRegion,
      }));
    }, [countryIsoCode, value?.stateOrRegion]);

    const timeZoneDropdownList = useMemo(() => {
      return timezones.map((tz) => ({
        ...tz,
        id: tz.tzCode,
        selected: tz.tzCode === value?.timeZone,
      }));
    }, [value?.timeZone]);

    const handlePlaceSelect = (place: any) => {
      if (!place) return;
      const mapped = mapGeoapifyToAddress(place, countryIsoCode);
      onChange({ ...mapped });
      setMode('manual');
    };

    const handleFieldChange = (key: keyof AddressValue, v: any) => {
      onChange({ [key]: v });
    };

    const isStateRequired = !!stateRequired;

    return (
      <div className='flex flex-col gap-2'>
        <div className='flex flex-row gap-1'>
          <Label
            text={labels?.addressLabel || t('location_address')}
            type={LabelType.LABEL_M}
            color={ColorType.BLACK}
          />
        </div>

        <div
          style={{
            display: mode === 'search' ? 'block' : 'none',
          }}
          className='address-autocomplete'
        >
          <GeoapifyGeocoderAutocomplete
            placeholder={labels?.searchPlaceholder || t('location_address_field_prompt')}
            debounceDelay={debounceMs}
            allowNonVerifiedHouseNumber
            placeSelect={handlePlaceSelect}
            skipSelectionOnArrowKey
            preprocessHook={(addr: any) => `${addr}, ${resolvedCountryName}`}
          />
          <Label
            text='Powered by Geoapify'
            type={LabelType.LABEL_XXS_MEDIUM}
            className='pt-1 text-grey5'
            onClick={() => window.open('https://www.geoapify.com/', '_geoapify')}
          />
          <Label
            className='pt-4'
            text={labels?.manualToggleLabel || t('location_manual_address')}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BRAND2}
            onClick={() => setMode('manual')}
          />
        </div>

        <div className={mode === 'manual' ? 'flex flex-col gap-[16px]' : 'hidden'}>
          <Label
            className='pt-0'
            text={labels?.backToSearchLabel || t('search_address')}
            type={LabelType.LABEL_S_MEDIUM}
            color={ColorType.BRAND2}
            onClick={() => setMode('search')}
          />
          <div className='flex flex-col gap-2'>
            <div className='flex flex-row gap-2'>
              <FormInput
                width='85px'
                placeholder={labels?.streetNumber || t('location_edit_street_number_holder')}
                onChange={(e) => handleFieldChange('streetNumber', e.target.value)}
                defaultValue={value?.streetNumber}
                dataTestId='addrStreetNumber'
                inputTitle={labels?.streetNumber || t('location_edit_street_number_holder')}
                validationQueryResult={!!errors?.streetNumber}
                showErrorIcon={!!errors?.streetNumber}
                errorLabel={errors?.streetNumber ? errorLabels?.streetNumber || '' : ''}
              />
              <div className='grow'>
                <FormInput
                  placeholder={
                    labels?.streetName || labels?.street || t('location_edit_street_name_holder')
                  }
                  onChange={(e) => handleFieldChange('street', e.target.value)}
                  defaultValue={value?.street}
                  width='100%'
                  dataTestId='addrStreetName'
                  inputTitle={
                    labels?.streetName || labels?.street || t('location_edit_street_name_holder')
                  }
                  validationQueryResult={!!errors?.street}
                  showErrorIcon={!!errors?.street}
                  errorLabel={errors?.street ? errorLabels?.street || '' : ''}
                />
              </div>
            </div>
            {include?.unit !== false && (
              <FormInput
                placeholder={t('location_edit_unit_holder')}
                onChange={(e) => handleFieldChange('unit', e.target.value)}
                defaultValue={value?.unit}
                width='100%'
                dataTestId='addrUnit'
                inputTitle={t('location_edit_unit_holder')}
              />
            )}
          </div>

          <div className='flex flex-row'>
            <div className='flex-1'>
              <FormInput
                inputId='addr_city'
                label={labels?.city || t('location_city')}
                placeholder={labels?.city || t('location_city_placeholder')}
                onChange={(e) => handleFieldChange('city', e.target.value)}
                defaultValue={value?.city}
                props={{ style: { width: '242px' } }}
                dataTestId='addrCity'
                validationQueryResult={!!errors?.city}
                showErrorIcon={!!errors?.city}
                errorLabel={errors?.city ? errorLabels?.city || '' : ''}
              />
            </div>
            <div className='flex-1 flex flex-col gap-2'>
              <FormInput
                inputId='addr_zip'
                label={labels?.zip || t('location_zip')}
                placeholder={labels?.zip || t('location_zip')}
                onChange={(e) => handleFieldChange('zipOrPostalCode', e.target.value)}
                defaultValue={value?.zipOrPostalCode}
                props={{ style: { width: '242px' } }}
                dataTestId='addrZipCode'
                validationQueryResult={!!errors?.zip}
                showErrorIcon={!!errors?.zip}
                errorLabel={errors?.zip ? errorLabels?.zip || '' : ''}
              />
            </div>
          </div>

          <div className='flex flex-row'>
            {include?.country !== false && (
              <div className='flex-1 flex flex-col gap-2'>
                <Label
                  text={labels?.country || t('location_country')}
                  type={LabelType.LABEL_M}
                  color={ColorType.BLACK}
                  labelHtmlFor='addr_country'
                />
                {countryDropdownItems && onCountryChange ? (
                  <Dropdown
                    placeholder={t('location_country_placeholder')}
                    headerWidth={242}
                    label='name'
                    items={countryDropdownItems}
                    onItemClick={onCountryChange}
                    labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                    chevdownIconHighlightColor={ColorType.GREY6}
                    headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px]'
                    contentDivHeight={300}
                    dataTestId='addrCountry'
                  />
                ) : (
                  <FormInput
                    inputId='addr_country'
                    width='242px'
                    defaultValue={resolvedCountryName}
                    props={{
                      disabled: true,
                      style: {
                        width: '242px',
                        color: 'rgb(209, 214, 219)',
                        backgroundColor: 'rgb(249, 250, 251)',
                      },
                    }}
                  />
                )}
              </div>
            )}
            {include?.state !== false && (
              <div className='flex-1 flex flex-col gap-2'>
                <div className='flex flex-row gap-2 items-baseline'>
                  <Label
                    text={labels?.state || t('location_state')}
                    type={LabelType.LABEL_M}
                    color={ColorType.BLACK}
                  />
                  {!isStateRequired && showStateOptionalHint && (
                    <Label text={t('optional')} type={LabelType.BODY3} color={ColorType.GREY6} />
                  )}
                </div>
                <Dropdown
                  placeholder={labels?.state || t('location_state_placeholder')}
                  headerWidth={242}
                  label='name'
                  items={provinceList}
                  disabled={!countryIsoCode}
                  onItemClick={(item: any) => handleFieldChange('stateOrRegion', item.isoCode)}
                  labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                  chevdownIconHighlightColor={ColorType.GREY6}
                  headerHighLightClassName='bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                  contentDivHeight={300}
                  dataTestId='addrStateOrProvince'
                />
              </div>
            )}
          </div>

          {include?.geo !== false && (
            <div className='flex flex-col gap-2'>
              <div className='flex flex-row gap-2'>
                <Label text={t('location_geo')} type={LabelType.LABEL_M} color={ColorType.BLACK} />
                <Tooltip>
                  <TooltipTrigger>
                    <Icon src={Info} size={IconSize.SIZE_20x20} />
                  </TooltipTrigger>
                  <TooltipContent>
                    <span className='text-white font-medium text-sm'>
                      <Trans i18nKey='location_geolocaiton_tip' components={{ br: <br /> }} />
                    </span>
                  </TooltipContent>
                </Tooltip>
              </div>
              <div className='flex flex-row'>
                <div className='flex-1 flex-col'>
                  <Label
                    text={labels?.lat || t('location_lat')}
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                    labelHtmlFor='addr_lat'
                  />
                  <FormInput
                    inputId='addr_lat'
                    width='242px'
                    placeholder={labels?.lat || t('location_lat_placeholder')}
                    onChange={(e) => handleFieldChange('latitude', e.target.value)}
                    defaultValue={value?.latitude}
                    inputType={InputType.GEOLOCATION}
                    validationType={ValidationType.GEO_LAT}
                    errorLabel={t('location_wrong_lat')}
                    dataTestId='addrLat'
                  />
                </div>
                <div className='flex-1'>
                  <Label
                    text={labels?.lon || t('location_lon')}
                    type={LabelType.BODY3}
                    color={ColorType.BLACK}
                    labelHtmlFor='addr_lon'
                  />
                  <FormInput
                    inputId='addr_lon'
                    width='242px'
                    placeholder={labels?.lon || t('location_lon_placeholder')}
                    onChange={(e) => handleFieldChange('longitude', e.target.value)}
                    defaultValue={value?.longitude}
                    inputType={InputType.GEOLOCATION}
                    validationType={ValidationType.GEO_LON}
                    errorLabel={t('location_wrong_lon')}
                    dataTestId='addrLong'
                  />
                </div>
              </div>
              <Label
                className='pt-1'
                text={t('check_geolocation')}
                type={LabelType.LABEL_S_MEDIUM}
                color={ColorType.BRAND2}
                onClick={() => {
                  window.open(
                    `https://maps.google.com/maps/search/?api=1&query=${value?.latitude},${value?.longitude}`,
                    '_clmapcheck',
                  );
                }}
              />
            </div>
          )}

          {include?.timezone !== false && (
            <div className='flex flex-col gap-2'>
              <Label
                text={labels?.timezone || t('location_timezone')}
                type={LabelType.LABEL_M}
                color={ColorType.BLACK}
              />
              <Dropdown
                placeholder={labels?.timezone || t('location_timezone_placeholder')}
                headerWidth='100%'
                contentDivWidth={500}
                items={timeZoneDropdownList}
                onItemClick={(item: any) => handleFieldChange('timeZone', item.tzCode)}
                labelTypeHighlighted={LabelType.DROPDOWN_HEADER}
                chevdownIconHighlightColor={ColorType.GREY6}
                headerHighLightClassName='hover:bg-grey2 bg-grey1 border-grey3 rounded h-[40px] mr-1.5'
                contentDivHeight={300}
                placement='top-start'
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);

AddressAutocomplete.displayName = 'AddressAutocomplete';
